import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { ExternalLink, Loader2, Building2, User, Briefcase, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  deal: Briefcase,
  investor: User,
  investor_profile: User,
  borrower: User,
  lender: Building2,
  fund: TrendingUp,
  opportunity: TrendingUp,
};

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700",
  approved: "bg-emerald-100 text-emerald-700",
  verified: "bg-emerald-100 text-emerald-700",
  published: "bg-blue-100 text-blue-700",
  pending: "bg-amber-100 text-amber-700",
  pending_review: "bg-amber-100 text-amber-700",
  draft: "bg-gray-100 text-gray-600",
  closed: "bg-gray-100 text-gray-600",
  rejected: "bg-red-100 text-red-700",
};

export default function EntityPreviewLink({ entityType, entityId, onNavigate, children }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const timerRef = useRef(null);
  const token = localStorage.getItem("ic_token");

  const fetchPreview = async () => {
    if (preview) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/entity/preview/${entityType}/${entityId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
    } catch {
      setPreview({ found: false, entity_type: entityType, entity_id: entityId });
    } finally {
      setLoading(false);
    }
  };

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => {
      fetchPreview();
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({ top: rect.bottom + 8, left: Math.min(rect.left, window.innerWidth - 320) });
      }
      setShow(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setShow(false), 200);
  };

  const handlePopoverEnter = () => clearTimeout(timerRef.current);
  const handlePopoverLeave = () => {
    timerRef.current = setTimeout(() => setShow(false), 150);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const Icon = TYPE_ICONS[entityType] || Briefcase;
  const displayId = entityId.length > 20 ? entityId.slice(0, 18) + "..." : entityId;
  const statusColor = preview?.status ? (STATUS_COLORS[preview.status] || STATUS_COLORS[preview.status?.toLowerCase()] || "bg-gray-100 text-gray-600") : "";

  return (
    <>
      <span ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="inline-flex">
        {children || (
          <button
            onClick={() => onNavigate?.()}
            className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-mono text-xs underline underline-offset-2 decoration-emerald-300 hover:decoration-emerald-600 transition-colors"
            data-testid={`entity-preview-link-${entityId}`}
          >
            {entityType}/{displayId}
            <ExternalLink className="h-3 w-3" />
          </button>
        )}
      </span>

      {show && (
        <div
          ref={popoverRef}
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverLeave}
          className="fixed z-[100] animate-in fade-in slide-in-from-top-1 duration-150"
          style={{ top: position.top, left: position.left }}
          data-testid={`entity-preview-popover-${entityId}`}
        >
          <div className="w-72 bg-white rounded-xl border border-[#e8e0d0] shadow-xl overflow-hidden">
            {loading ? (
              <div className="p-4 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
              </div>
            ) : !preview?.found ? (
              <div className="p-4 text-center">
                <p className="text-xs text-[#999]">Entity not found in system</p>
                <p className="text-[10px] text-[#bbb] mt-1 font-mono">{entityType}/{entityId}</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="px-4 pt-3 pb-2 bg-[#faf7f2] border-b border-[#e8e0d0]">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-white border border-[#e8e0d0]">
                      <Icon className="h-3.5 w-3.5 text-emerald-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#222] truncate">{preview.title}</p>
                      {preview.subtitle && <p className="text-[10px] text-[#888] truncate">{preview.subtitle}</p>}
                    </div>
                    {preview.status && (
                      <span className={`px-2 py-0.5 rounded-full text-[8px] font-bold uppercase ${statusColor}`}>
                        {preview.status.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                </div>
                {/* Details */}
                {preview.details && Object.keys(preview.details).length > 0 && (
                  <div className="px-4 py-2.5 space-y-1">
                    {Object.entries(preview.details).map(([k, v]) => (
                      <div key={k} className="flex items-baseline justify-between">
                        <span className="text-[10px] text-[#999]">{k}</span>
                        <span className="text-[11px] font-semibold text-[#333]">{v}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Footer */}
                <div className="px-4 py-2 border-t border-[#e8e0d0] bg-[#faf7f2]/50">
                  <button
                    onClick={() => { setShow(false); onNavigate?.(); }}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-[10px] font-bold hover:bg-emerald-600 transition-colors"
                    data-testid={`entity-preview-open-${entityId}`}
                  >
                    <ExternalLink className="h-3 w-3" /> Open Full 360 View
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
